import React, { useState, useEffect } from 'react';
import {
    apiGetInsuranceSettings,
    apiSaveInsuranceSettings,
    apiGetBasicCodeOptions,
    apiCalculateInsurance,
    apiConfirmInsurance,
    apiGetEmployeesByGroup,
    apiGetGroups
} from '../../api';
import { message, Select, InputNumber, Tabs, Input } from 'antd';
import dayjs from 'dayjs';

const { Option } = Select;

// --- Helper Functions ---
const formatDisplayNumber = (value) => {
    if (value === null || value === undefined || isNaN(value) || value === 0) return '-';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const formatInputNumber = (value) => {
    if (value === null || value === undefined || isNaN(value) || Number(value) === 0) return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const inputParser = (value) => value.replace(/\./g, '');

const formatDate = (dateString) => {
    if (!dateString) return '-';
    if (dateString.includes('/')) return dateString;
    const date = dayjs(dateString);
    return date.isValid() ? date.format('YYYY/MM/DD') : '-';
};

// --- Component 1: Cài đặt Tỷ lệ Bảo hiểm ---
const InsuranceRateSettings = () => {
    const [yymm, setYymm] = useState(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`);
    const [whsno, setWhsno] = useState('VN');
    const [whsnoOptions, setWhsnoOptions] = useState([]);
    const [calculationItems, setCalculationItems] = useState(null);
    const [rates, setRates] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const maxDate = `${new Date().getFullYear() + 10}-12`; // Cho phép chọn 10 năm tới

    const itemLabels = {
        BB: 'LƯƠNG CB (BB)', CV: 'CHỨC VỤ (CV)', PHU: 'ĐIỆN THOẠI (PHU)',
        NN: 'XĂNG XE (NN)', KT: 'KỸ THUẬT (KT)', MT: 'MÔI TRƯỜNG (MT)', BTIEN: 'BÙ LƯƠNG (BTIEN)'
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const whsnoRes = await apiGetBasicCodeOptions('WHSNO');
                setWhsnoOptions(Array.isArray(whsnoRes) ? whsnoRes : []);
            } catch (error) {
                message.error("Lỗi khi tải danh sách xưởng.");
            }
        };
        fetchInitialData();
    }, []);
    
    const handleFetchSettings = async () => {
        if (!yymm || !whsno) {
            message.warning('Vui lòng chọn xưởng và kỳ lương.');
            return;
        }
        setLoading(true);
        const year = yymm.substring(0, 4);
        const month = yymm.substring(5, 7);
        const yymmForApi = `${year}${month}`;

        try {
            const data = await apiGetInsuranceSettings(yymmForApi, whsno);
            if (data && data.calculationItems && data.rates) {
                setCalculationItems(data.calculationItems);
                setRates(data.rates);
            } else {
                 setCalculationItems({ BB: false, CV: false, PHU: false, NN: false, KT: false, MT: false, BTIEN: false });
                 setRates({
                    vn: { emp_bhxh: 0, emp_bhyt: 0, emp_bhtn: 0, cty_bhxh: 0, cty_bhyt: 0, cty_bhtn: 0, emp_gtamt: 0, gt_per: 0 },
                    hw: { emp_bhxh: 0, emp_bhyt: 0, emp_bhtn: 0, cty_bhxh: 0, cty_bhyt: 0, cty_bhtn: 0 }
                });
            }
        } catch (error) {
            message.error("Lỗi khi tải cài đặt bảo hiểm.");
            setCalculationItems(null);
            setRates(null);
        } finally {
            setLoading(false);
        }
    };
    
    const handleRateChange = (country, type, value) => {
        setRates(prevRates => ({ ...prevRates, [country]: { ...prevRates[country], [type]: parseFloat(value) || 0 } }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        const year = yymm.substring(0, 4);
        const month = yymm.substring(5, 7);
        const yymmForApi = `${year}${month}`;
        try {
            await apiSaveInsuranceSettings({ yymm: yymmForApi, whsno, calculationItems, rates });
            message.success("Lưu dữ liệu thành công!");
        } catch (error) {
            message.error("Lỗi khi lưu cài đặt bảo hiểm.");
        } finally {
            setIsSaving(false);
        }
    };

    const renderRateTable = (title, country, rateData) => (
        <div className="border border-gray-200 p-4 rounded-lg bg-white h-full shadow-sm hover:shadow-lg transition-shadow duration-300">
            <h3 className="text-xl font-semibold text-center mb-6 text-gray-700">{title}</h3>
            <div className="flex flex-wrap -mx-2">
                <div className="w-1/2 px-2 space-y-2">
                    <strong className="text-blue-600">Nhân viên</strong>
                    <div className="flex flex-wrap -mx-1">
                        <div className="w-1/2 px-1 mb-2"><span>BHXH%</span><InputNumber className="w-full" style={{ fontFamily: 'inherit' }} value={rateData.emp_bhxh} onChange={v => handleRateChange(country, 'emp_bhxh', v)} /></div>
                        <div className="w-1/2 px-1 mb-2"><span>BHYT%</span><InputNumber className="w-full" style={{ fontFamily: 'inherit' }} value={rateData.emp_bhyt} onChange={v => handleRateChange(country, 'emp_bhyt', v)} /></div>
                        <div className="w-full px-1"><span>BHTN%</span><InputNumber className="w-full" style={{ fontFamily: 'inherit' }} value={rateData.emp_bhtn} onChange={v => handleRateChange(country, 'emp_bhtn', v)} /></div>
                    </div>
                </div>
                <div className="w-1/2 px-2 space-y-2">
                    <strong className="text-blue-600">Công ty</strong>
                     <div className="flex flex-wrap -mx-1">
                        <div className="w-1/2 px-1 mb-2"><span>BHXH%</span><InputNumber className="w-full" style={{ fontFamily: 'inherit' }} value={rateData.cty_bhxh} onChange={v => handleRateChange(country, 'cty_bhxh', v)} /></div>
                        <div className="w-1/2 px-1 mb-2"><span>BHYT%</span><InputNumber className="w-full" style={{ fontFamily: 'inherit' }} value={rateData.cty_bhyt} onChange={v => handleRateChange(country, 'cty_bhyt', v)} /></div>
                        <div className="w-full px-1"><span>BHTN%</span><InputNumber className="w-full" style={{ fontFamily: 'inherit' }} value={rateData.cty_bhtn} onChange={v => handleRateChange(country, 'cty_bhtn', v)} /></div>
                    </div>
                </div>
                {country === 'vn' && (
                    <div className="w-full px-2 mt-4 space-y-2">
                        <strong className="text-blue-600">Công đoàn</strong>
                        <div className="flex flex-wrap -mx-1">
                            <div className="w-1/2 px-1"><span>Phí CĐ</span><InputNumber className="w-full" style={{ fontFamily: 'inherit' }} value={rateData.emp_gtamt} onChange={v => handleRateChange(country, 'emp_gtamt', v)} /></div>
                            <div className="w-1/2 px-1"><span>Tạm giữ %</span><InputNumber className="w-full" style={{ fontFamily: 'inherit' }} value={rateData.gt_per} onChange={v => handleRateChange(country, 'gt_per', v)} /></div>
                        </div>
                    </div>
                 )}
            </div>
        </div>
    );

    return (
        <div className="space-y-6 p-4 bg-gray-50 min-h-screen" style={{ fontFamily: '"Times New Roman", Times, serif' }}>
            <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600 mb-4 text-center">
                CÀI ĐẶT TỶ LỆ BẢO HIỂM
            </h2>
            <div className="bg-white p-4 rounded-lg shadow-md flex flex-wrap items-end gap-4 justify-center">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Chọn Tháng</label>
                    <input type="month" value={yymm} max={maxDate} onChange={e => setYymm(e.target.value)} className="mt-1 p-2 border rounded-md" style={{ fontFamily: 'inherit' }}/>
                </div>
                 <div>
                    <label className="block text-sm font-medium text-gray-700">Chọn Xưởng</label>
                    <select value={whsno} onChange={e => setWhsno(e.target.value)} className="mt-1 block w-40 p-2 border rounded-md" style={{ fontFamily: 'inherit' }}>
                        {whsnoOptions.map(opt => <option key={opt.SYS_TYPE} value={opt.SYS_TYPE}>{opt.SYS_VALUE}</option>)}
                    </select>
                 </div>
                 <button onClick={handleFetchSettings} disabled={loading} className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 shadow hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-150">
                    {loading ? 'Đang tải...' : 'Tải Cài Đặt'}
                 </button>
            </div>
            
            {rates && calculationItems && (
                <>
                    <div className="border border-blue-200 p-4 rounded-lg mb-4 bg-white shadow-sm">
                         <h3 className="text-lg font-semibold mb-3 text-indigo-700 border-b pb-2">Hạng mục tham gia bảo hiểm</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 pt-2">
                            {Object.keys(calculationItems).map(item => (
                                <label key={item} className="flex items-center space-x-2 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={calculationItems[item]} 
                                        onChange={(e) => setCalculationItems({...calculationItems, [item]: e.target.checked})}
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span>{itemLabels[item] || item}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-wrap -mx-2">
                        <div className="w-full md:w-1/2 px-2 mb-4">{renderRateTable('(VN) Nhân viên / Công ty', 'vn', rates.vn)}</div>
                        <div className="w-full md:w-1/2 px-2 mb-4">{renderRateTable('(Nước ngoài) Nhân viên / Công ty', 'hw', rates.hw)}</div>
                    </div>
                    <div className="flex justify-end mt-6">
                        <button onClick={handleSave} disabled={isSaving || loading} className="px-8 py-3 font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-400 text-base shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200">
                            {isSaving ? 'Đang lưu...' : 'Xác nhận & Lưu Cài đặt'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

// --- Component 2: Tính toán & Xác nhận Bảo hiểm ---
const InsuranceCalculation = () => {
    const [selectedMonth, setSelectedMonth] = useState(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`);
    const [selectedGroup, setSelectedGroup] = useState('ALL');
    const [selectedEmp, setSelectedEmp] = useState('ALL');
    const [selectedWhsno, setSelectedWhsno] = useState('VN'); 
    
    const [groups, setGroups] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [insuranceData, setInsuranceData] = useState([]);
    const [whsnoOptions, setWhsnoOptions] = useState([]); 

    const [loading, setLoading] = useState(false);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const maxDate = `${new Date().getFullYear() + 10}-12`; // Cho phép chọn 10 năm tới

    const tableHeaders = [
        { key: 'stt', label: 'STT', isFixed: 'left', width: '60px', style: { left: 0 } },
        { key: 'EMPID', label: 'Mã NV', isFixed: 'left', width: '100px', style: { left: '60px' } },
        { key: 'EMPNAM_VN', label: 'Họ Tên', isFixed: 'left', width: '200px', style: { left: '160px' } },
        { key: 'INDAT', label: 'Ngày vào', width: '120px' },
        { key: 'OUTDAT', label: 'Ngày nghỉ', width: '120px' },
        { key: 'lncode', label: 'Bậc Lương', width: '100px' },
        { key: 'N_BB', label: 'Lương CB', isNumeric: true, width: '120px' },
        { key: 'N_cv', label: 'PC Chức vụ', isNumeric: true, width: '120px' },
        { key: 'MT', label: 'Môi trường', isNumeric: true, width: '120px' },
        { key: 'KT', label: 'Kỹ thuật', isNumeric: true, width: '120px' },
        { key: 'BHT1', label: 'Phí BH', isNumeric: true, width: '120px' },
        { key: 'BHDAT', label: 'Ngày BH', width: '120px' },
        { key: 'BHXH', label: 'BHXH', isEditable: true, isNumeric: true, width: '120px' },
        { key: 'BHYT', label: 'BHYT', isEditable: true, isNumeric: true, width: '120px' },
        { key: 'BHTN', label: 'BHTN', isEditable: true, isNumeric: true, width: '120px' },
        { key: 'BHTOT', label: 'Tổng BH', isNumeric: true, width: '120px' },
        { key: 'GTDAT', label: 'Ngày vào CĐ', width: '120px' },
        { key: 'GTAMT', label: 'Phí CĐ', isEditable: true, isNumeric: true, width: '120px' },
        { key: 'bhmemo', label: 'Ghi chú', isEditable: true, isText: true, width: '200px' }
    ];

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoadingGroups(true);
            try {
                const [groupsData, whsnoData] = await Promise.all([
                    apiGetGroups(),
                    apiGetBasicCodeOptions('WHSNO')
                ]);
                setGroups(groupsData);
                setWhsnoOptions(Array.isArray(whsnoData) ? whsnoData : []);
            } catch (error) {
                message.error('Không thể tải danh sách bộ phận hoặc xưởng.');
            } finally {
                setLoadingGroups(false);
            }
        };
        fetchInitialData();
    }, []);
    
    useEffect(() => {
        if (selectedGroup) {
            handleGroupChange(selectedGroup);
        }
    }, [selectedMonth]);

    const handleGroupChange = async (groupid) => {
        setSelectedGroup(groupid);
        if (!selectedMonth) {
            message.warning('Vui lòng chọn tháng trước.');
            setEmployees([]);
            return;
        }
        
        const year = selectedMonth.substring(0, 4);
        const month = selectedMonth.substring(5, 7);
        const monthYearForAPI = `${month}${year}`;

        try {
            setLoadingEmployees(true);
            const data = await apiGetEmployeesByGroup(groupid, monthYearForAPI);
            setEmployees(data);
            setSelectedEmp('ALL');
        } catch (error) {
            message.error('Không thể tải danh sách nhân viên.');
        } finally {
            setLoadingEmployees(false);
        }
    };

    const handleCalculate = async () => {
        if (!selectedMonth) {
            message.error('Vui lòng chọn tháng.');
            return;
        }
        setHasSearched(true);
        setLoading(true);
        setInsuranceData([]);
        try {
            const year = selectedMonth.substring(0, 4);
            const month = selectedMonth.substring(5, 7);
            const calculationFilters = { 
                yymm: `${year}${month}`, 
                whsno: selectedWhsno,
                groupid: selectedGroup, 
                empid1: selectedEmp,
                COUNTRY: 'VN' 
            }; 
            const data = await apiCalculateInsurance(calculationFilters);
            setInsuranceData(data);
            if (data.length > 0) {
                message.success(`Đã tính toán dữ liệu cho ${data.length} nhân viên!`);
            } else {
                message.info("Không tìm thấy dữ liệu nhân viên với bộ lọc đã chọn.");
            }
        } catch (error) {
            message.error(error.message || "Lỗi khi tính toán bảo hiểm.");
        } finally {
            setLoading(false);
        }
    };
    
    const handleConfirm = async () => {
        setIsConfirming(true);
        try {
            const year = selectedMonth.substring(0, 4);
            const month = selectedMonth.substring(5, 7);
            await apiConfirmInsurance({ yymm: `${year}${month}`, employeeData: insuranceData, muser: 'USER_APP' });
            
            // Hiện thông báo thành công
            message.success("Lưu dữ liệu thành công!");
            
            // XÓA DỮ LIỆU HIỂN THỊ SAU KHI LƯU
            setInsuranceData([]);
            setHasSearched(false); 
            
        } catch (error) {
            message.error("Lỗi khi xác nhận bảo hiểm.");
        } finally {
            setIsConfirming(false);
        }
    };
    
    const handleEmployeeDataChange = (index, field, value) => {
        const updatedData = [...insuranceData];
        updatedData[index][field] = value;
        if (['BHXH', 'BHYT', 'BHTN'].includes(field)) {
             updatedData[index]['BHTOT'] = (updatedData[index]['BHXH'] || 0) + (updatedData[index]['BHYT'] || 0) + (updatedData[index]['BHTN'] || 0);
        }
        setInsuranceData(updatedData);
    };

    return (
        <div className="space-y-6 p-4" style={{ fontFamily: '"Times New Roman", Times, serif' }}>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">TÍNH TOÁN & XÁC NHẬN BẢO HIỂM</h2>
            
            <div className="bg-gray-50 p-4 rounded-lg flex flex-wrap items-end gap-4 mb-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Chọn Tháng</label>
                    <input type="month" value={selectedMonth} max={maxDate} onChange={e => setSelectedMonth(e.target.value)} className="mt-1 p-2 border rounded-md" style={{ fontFamily: 'inherit' }}/>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Chọn Xưởng</label>
                    <select value={selectedWhsno} onChange={e => setSelectedWhsno(e.target.value)} className="mt-1 block w-40 p-2 border rounded-md" style={{ fontFamily: 'inherit' }}>
                        {whsnoOptions.map(opt => (
                            <option key={opt.SYS_TYPE} value={opt.SYS_TYPE}>
                                {opt.SYS_VALUE}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Chọn Bộ phận</label>
                    <select value={selectedGroup} onChange={e => handleGroupChange(e.target.value)} disabled={loadingGroups} className="mt-1 block w-52 p-2 border rounded-md" style={{ fontFamily: 'inherit' }}>
                        <option value="ALL">Tất cả bộ phận</option>
                        {groups.map(g => (
                            <option key={g.groupId} value={g.groupId}>
                                {g.groupName} ({g.groupId})
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Chọn Nhân viên</label>
                    <Select
                        showSearch
                        value={selectedEmp}
                        onChange={v => setSelectedEmp(v)}
                        style={{ width: 240, fontFamily: 'inherit' }}
                        placeholder="Tìm và chọn nhân viên"
                        loading={loadingEmployees}
                        disabled={loadingEmployees || !employees.length}
                        filterOption={(input, option) => 
                            (option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
                        }
                        className="mt-1"
                    >
                        <Option value="ALL">Tất cả nhân viên</Option>
                        {employees.map(emp => (
                            <Option key={emp.empid} value={emp.empid}>
                                {`${emp.name} (${emp.empid})`}
                            </Option>
                        ))}
                    </Select>
                </div>
                <button onClick={handleCalculate} disabled={loading} className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                    {loading ? 'Đang tính...' : 'Bắt đầu tính'}
                </button>
            </div>
            
            {hasSearched && (
            <>
                <div className="overflow-auto bg-white rounded-lg shadow border" style={{ maxHeight: '70vh' }}>
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr>
                                {tableHeaders.map(header => (
                                    <th key={header.key} 
                                        className={`p-2 font-semibold text-gray-600 whitespace-nowrap border-b border-r sticky top-0 bg-gray-100 ${header.isFixed ? 'z-30' : 'z-20'} ${header.isNumeric ? 'text-right' : 'text-left'}`}
                                        style={{ width: header.width, minWidth: header.width, ...header.style }}>
                                        {header.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan={tableHeaders.length} className="p-4 text-center">Đang tải dữ liệu...</td></tr>
                            ) : insuranceData.length > 0 ? (
                                insuranceData.map((row, index) => (
                                <tr key={row.EMPID} className="hover:bg-gray-50">
                                    {tableHeaders.map(header => (
                                        <td key={`${row.EMPID}-${header.key}`}
                                            className={`
                                                whitespace-nowrap border-r
                                                ${header.isFixed ? 'sticky z-10' : ''}
                                                ${(header.isNumeric && !header.isEditable) || header.key === 'stt' ? 'p-2' : 'p-0'}
                                                ${header.isFixed ? 'bg-white hover:bg-gray-50' : ''}
                                                ${header.isNumeric ? 'text-right' : 'text-left'}
                                            `}
                                            style={{ ...header.style }}>
                                            {(() => {
                                                if (header.key === 'stt') return <div className="text-center">{index + 1}</div>;
                                                if (['INDAT', 'OUTDAT', 'BHDAT', 'GTDAT'].includes(header.key)) return <div className="p-2">{formatDate(row[header.key])}</div>;
                                                
                                                if (header.isEditable) {
                                                    if (header.isText) {
                                                        return <Input bordered={false} style={{ width: '100%', backgroundColor: 'transparent', fontFamily: 'inherit' }} value={row[header.key]} onChange={e => handleEmployeeDataChange(index, header.key, e.target.value)} />;
                                                    }
                                                    return <InputNumber value={row[header.key]} onChange={v => handleEmployeeDataChange(index, header.key, v)} bordered={false} style={{ width: '100%', backgroundColor: 'transparent', fontFamily: 'inherit' }} className="text-right" formatter={formatInputNumber} parser={inputParser} controls={false} />;
                                                }

                                                if (header.isNumeric) return <div className="p-2">{formatDisplayNumber(row[header.key])}</div>;

                                                return <div className="p-2">{row[header.key]}</div>;
                                            })()}
                                        </td>
                                    ))}
                                </tr>
                                ))
                            ) : (
                                <tr><td colSpan={tableHeaders.length} className="p-4 text-center text-gray-500">Không tìm thấy dữ liệu.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {insuranceData.length > 0 && (
                    <div className="flex justify-end mt-6">
                        <button onClick={handleConfirm} disabled={isConfirming || loading} className="px-8 py-3 font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-400 text-base">
                            {isConfirming ? 'Đang lưu...' : 'Xác nhận & Lưu'}
                        </button>
                    </div>
                )}
            </>
            )}
        </div>
    );
};

// --- Main Component ---
const SocialInsurance = () => {
    const items = [
        { key: '1', label: <span style={{ fontFamily: '"Times New Roman", Times, serif' }}>Cài đặt Tỷ lệ</span>, children: <InsuranceRateSettings /> },
        { key: '2', label: <span style={{ fontFamily: '"Times New Roman", Times, serif' }}>Tính toán & Xác nhận</span>, children: <InsuranceCalculation /> },
    ];
    return (
        <div>
            <Tabs defaultActiveKey="1" centered items={items} />
        </div>
    );
};

export default SocialInsurance;